/* Contractor fleet map — only the contractor's linked trucks, their plants and active job sites. */
import { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader, TrafficLayer, OverlayView } from "@react-google-maps/api";
import { Factory, MapPin } from "lucide-react";
import { INDUSTRIAL_MAP_STYLE, STATUS_TINT, truckPinDataUrl } from "@/lib/mapTheme";

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const LIBS = [];
const FALLBACK = { lat: -42.835973, lng: 147.450342 };

export function ContractorFleetMap({ trucks, plants, selectedId, onSelect }) {
  const { isLoaded } = useJsApiLoader({ id: "gadtech-war-room", googleMapsApiKey: GOOGLE_KEY, libraries: LIBS });
  const mapRef = useRef(null);
  const fitted = useRef(false);
  const [displayed, setDisplayed] = useState({});
  const targets = useRef({});

  useEffect(() => {
    const next = { ...targets.current };
    trucks.forEach((t) => { if (t.lat != null && t.lng != null) next[t.id] = { lat: t.lat, lng: t.lng }; });
    targets.current = next;
    setDisplayed((prev) => {
      const seed = { ...prev };
      trucks.forEach((t) => { if (t.lat != null && t.lng != null && !seed[t.id]) seed[t.id] = { lat: t.lat, lng: t.lng }; });
      return seed;
    });
  }, [trucks]);

  useEffect(() => {
    let raf;
    const tick = () => {
      setDisplayed((prev) => {
        let changed = false; const next = { ...prev };
        for (const id of Object.keys(targets.current)) {
          const tgt = targets.current[id]; const cur = prev[id];
          if (!cur) { next[id] = tgt; changed = true; continue; }
          const dLat = tgt.lat - cur.lat, dLng = tgt.lng - cur.lng;
          if (Math.abs(dLat) < 1e-7 && Math.abs(dLng) < 1e-7) continue;
          next[id] = { lat: cur.lat + dLat * 0.06, lng: cur.lng + dLng * 0.06 }; changed = true;
        }
        return changed ? next : prev;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const sites = useMemo(() => {
    const m = {};
    trucks.forEach((t) => { if (t.job?.site_lat != null && t.job?.site_lng != null) m[t.job.id] = t.job; });
    return Object.values(m);
  }, [trucks]);

  const center = useMemo(() => {
    const pts = Object.values(displayed);
    if (pts.length) return { lat: pts.reduce((s, p) => s + p.lat, 0) / pts.length, lng: pts.reduce((s, p) => s + p.lng, 0) / pts.length };
    const p = plants.find((x) => x.lat != null);
    return p ? { lat: p.lat, lng: p.lng } : FALLBACK;
  }, [displayed, plants]);

  useEffect(() => {
    if (!mapRef.current || fitted.current || !window.google) return;
    const pts = [...trucks.filter((t) => t.lat != null), ...plants.filter((p) => p.lat != null)];
    if (pts.length < 2) return;
    const b = new window.google.maps.LatLngBounds();
    pts.forEach((p) => b.extend({ lat: p.lat, lng: p.lng }));
    mapRef.current.fitBounds(b, 60);
    fitted.current = true;
  }, [trucks, plants]);

  useEffect(() => {
    if (!selectedId || !mapRef.current) return;
    const t = trucks.find((x) => x.id === selectedId);
    if (t && t.lat != null) mapRef.current.panTo({ lat: t.lat, lng: t.lng });
  }, [selectedId, trucks]);

  if (!GOOGLE_KEY) return <div className="h-full flex items-center justify-center text-red-400 mono text-sm">REACT_APP_GOOGLE_MAPS_API_KEY missing</div>;
  if (!isLoaded) return <div className="h-full flex items-center justify-center text-[#8B909A] mono text-xs">Loading map…</div>;

  return (
    <GoogleMap mapContainerStyle={{ width: "100%", height: "100%" }} center={center} zoom={11}
      onLoad={(m) => { mapRef.current = m; }}
      options={{ disableDefaultUI: false, streetViewControl: false, mapTypeControl: false, fullscreenControl: true, clickableIcons: false, styles: INDUSTRIAL_MAP_STYLE, backgroundColor: "#1F2126" }}>
      <TrafficLayer autoUpdate />
      {plants.map((p) => (
        <OverlayView key={`plant-${p.id}`} position={{ lat: p.lat, lng: p.lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
          <div className="translate-x-[-50%] translate-y-[-50%] pointer-events-none" title={p.name} data-testid={`ctr-map-plant-${p.code || p.id}`}>
            <div className="w-6 h-6 bg-[#F97316] border-2 border-black flex items-center justify-center shadow-md"><Factory className="w-3 h-3 text-black" /></div>
            <div className="text-[9px] mono font-black bg-black/80 text-white px-1 whitespace-nowrap -mt-0.5">{p.name}</div>
          </div>
        </OverlayView>
      ))}
      {sites.map((s) => (
        <OverlayView key={`site-${s.id}`} position={{ lat: s.site_lat, lng: s.site_lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
          <div className="translate-x-[-50%] translate-y-[-100%] pointer-events-none" title={s.site_name}>
            <MapPin className="w-6 h-6 text-[#A855F7] drop-shadow" fill="#A855F7" stroke="#0A0A0B" />
          </div>
        </OverlayView>
      ))}
      {trucks.map((t) => {
        const p = displayed[t.id];
        if (!p) return null;
        const sel = selectedId === t.id;
        return (
          <OverlayView key={`truck-${t.id}`} position={p} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
            <button type="button" onClick={() => onSelect(t.id)} data-testid={`ctr-map-truck-${t.number}`} title={`${t.number} · ${t.status}`}
              className={`translate-x-[-50%] translate-y-[-50%] block transition-transform ${sel ? "scale-125" : "hover:scale-110"}`}>
              <img src={truckPinDataUrl(t.gps_dark ? "#EF4444" : (STATUS_TINT[t.status] || "#F97316"), sel, t.heading)} alt={t.number} className="w-11 h-11" draggable={false} />
              <div className="text-[10px] mono font-black bg-black/80 text-white px-1 py-0.5 -mt-1 whitespace-nowrap text-center">{t.number}</div>
            </button>
          </OverlayView>
        );
      })}
    </GoogleMap>
  );
}
